// src/Browser.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  useNotification,
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  notifyPermissionError,
} from '@1823-partners/core';
import {
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

// Import API functions from your index.js (or api module)
import api, { fetchFiles, uploadFile, downloadFile } from './api';

function Browser({ canUpload = false }) {
  const { showNotification } = useNotification();
  const [fileList, setFileList] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState('home/');
  const [pathHistory, setPathHistory] = useState(['home/']);
  const gridRef = useRef(null);

  // Own the per-user grid-config hook here (rather than letting
  // GridConfigMenu instantiate it internally) so we can bridge
  // gridConfigs.onGridReady to PamGrid's onGridReady — without that bridge a
  // grid that mounts AFTER the configs response would miss the default-layout
  // auto-apply. Reuses the same gridRef already passed to <PamGrid ref=…/>.
  const gridConfigs = useGridConfigs(api, {
    app: 'browser',
    gridId: 'records',
    gridApiRef: gridRef,
  });

  // GridConfigMenu is stateful (dropdown + dialog) and needs the gridRef, so
  // it rides through PamGrid's customToolbarComponents slot.
  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // When the component mounts, fetch the file list from the backend.
  useEffect(() => {
    fetchFilesHandler();
  }, [currentPath]);

  // Fetch file list using the imported API
  const fetchFilesHandler = async () => {
    setLoading(true);
    try {
      const response = await fetchFiles(currentPath);
      // Process dates to ensure they're in the correct format for filtering
      const processedData = response.data.map(item => ({
        ...item,
        last_modified: item.last_modified ? new Date(item.last_modified) : null
      }));
      setFileList(processedData);
      showNotification('Files loaded successfully', 'success');
    } catch (error) {
      console.error('Error fetching files:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification('Failed to load files', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Update state when files are selected
  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };

  // Handle dropped files
  const onDrop = useCallback(acceptedFiles => {
    setSelectedFiles(acceptedFiles);
    showNotification(`${acceptedFiles.length} file(s) selected for upload`, 'info');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // Upload files using the API function
  const handleUpload = async () => {
    if (!selectedFiles.length) {
      showNotification('No files selected for upload', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Use the updated API that accepts an array of files
      await uploadFile(selectedFiles, currentPath);
      
      // Reset the file input value
      document.getElementById("file-input").value = "";
      setSelectedFiles([]);
      // Refresh the file list after upload
      fetchFilesHandler();
      showNotification(`${selectedFiles.length} file(s) uploaded successfully`, 'success');
    } catch (error) {
      console.error("Error uploading files:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification('Failed to upload files', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Download file using the API function. The API returns a blob.
  const handleDownload = async (filename) => {
    setLoading(true);
    try {
      const response = await downloadFile(filename, currentPath);
      // Create a temporary URL for the binary data.
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showNotification('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading file:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification('Failed to download file', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle path change
  const handlePathChange = (event) => {
    const newPath = event.target.value;
    setCurrentPath(newPath);
    setPathHistory([...pathHistory.filter((path, index) => index < pathHistory.indexOf(currentPath) + 1), newPath]);
  };

  // Navigate to a directory
  const navigateToDirectory = (dirPath) => {
    setCurrentPath(dirPath);
    setPathHistory([...pathHistory, dirPath]);
  };

  // Navigate back to previous directory
  const navigateBack = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop(); // Remove current path
      const previousPath = newHistory[newHistory.length - 1];
      setCurrentPath(previousPath);
      setPathHistory(newHistory);
    }
  };

  // Navigate to specific breadcrumb
  const navigateToBreadcrumb = (index) => {
    const newPath = pathHistory[index];
    setCurrentPath(newPath);
    setPathHistory(pathHistory.slice(0, index + 1));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Check if item is a directory
  const isDirectory = (item) => {
    return item.type === 'directory';
  };

  // AG Grid column definitions
  const columnDefs = [
    { 
      headerName: 'Filename', 
      field: 'path', 
      valueGetter: (params) => {
        const path = params.data.path;
        if (!path) return params.data.filename || params.data.Key || 'N/A';
        
        const pathParts = path.split('/');
        // Extract the second-to-last part of the path (folder name)
        // or the last part if it's a file
        return isDirectory(params.data) ? 
          (pathParts.length > 1 ? pathParts[pathParts.length - 2] : path) : 
          pathParts[pathParts.length - 1];
      },
      cellRenderer: (params) => {
        const filename = params.value;
        const isDir = isDirectory(params.data);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isDir ? 
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FolderIcon color="primary" sx={{ mr: 1 }} />
                <Link 
                  component="button" 
                  variant="body2" 
                  onClick={() => navigateToDirectory(params.data.path)}
                  sx={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                  {filename}
                </Link>
              </Box>
              : filename
            }
          </Box>
        );
      },
      sortable: true, 
      filter: 'agTextColumnFilter',
      flex: 2,
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
        return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      }
    },
    { 
      headerName: 'Size', 
      field: 'size', 
      valueGetter: (params) => params.data.size || 0,
      valueFormatter: (params) => formatFileSize(params.value),
      sortable: true, 
      filter: 'agNumberColumnFilter',
      flex: 1
    },
    {
      headerName: 'Last Modified', 
      field: 'last_modified', 
      valueGetter: (params) => params.data.last_modified,
      valueFormatter: (params) => formatDate(params.value),
      sortable: true, 
      filter: 'agDateColumnFilter',
      filterParams: {
        // Configure date filter
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return 0;
          
          const filterDate = filterLocalDateAtMidnight.getTime();
          const cellDate = new Date(cellValue).getTime();
          
          if (cellDate < filterDate) {
            return -1;
          } else if (cellDate > filterDate) {
            return 1;
          }
          return 0;
        }
      },
      // Add a comparator for sorting the dates
      comparator: (date1, date2) => {
        if (!date1 && !date2) return 0;
        if (!date1) return -1;
        if (!date2) return 1;
        // Convert date objects to timestamps for numeric comparison
        return new Date(date1).getTime() - new Date(date2).getTime();
      },
      flex: 1.5
    },    
    { 
      headerName: 'Type', 
      field: 'type', 
      valueGetter: (params) => params.data.type || 'N/A',
      sortable: true, 
      filter: 'agTextColumnFilter',
      flex: 1,
      comparator: (valueA, valueB) => {
        return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      }
    },
    { 
      headerName: 'Path', 
      field: 'path', 
      valueGetter: (params) => params.data.path || 'N/A',
      sortable: true, 
      filter: 'agTextColumnFilter',
      flex: 2,
      comparator: (valueA, valueB) => {
        return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      }
    },
    {
      headerName: 'Actions',
      sortable: false,
      filter: false,
      flex: 1,
      cellRenderer: (params) => {
        const filename = params.data.path;
        const isDir = isDirectory(params.data);
        
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isDir ? (
              <Tooltip title="Open directory">
                <IconButton 
                  color="primary" 
                  onClick={() => navigateToDirectory(filename)}
                  size="small"
                >
                  <FolderIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Download file">
                <IconButton 
                  color="primary" 
                  onClick={() => handleDownload(filename)}
                  size="small"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete file">
              <IconButton color="error" size="small" disabled={true}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom style={{ marginTop: '20px' }}>
        1823 S3 Browser
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          {pathHistory.map((path, index) => (
            <Link
              key={index}
              color={index === pathHistory.length - 1 ? "text.primary" : "inherit"}
              onClick={() => navigateToBreadcrumb(index)}
              sx={{ cursor: 'pointer' }}
            >
              {path.split('/').pop() || path}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>
      
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={navigateBack}
          disabled={pathHistory.length <= 1}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Tooltip title="Refresh files">
            <IconButton onClick={fetchFilesHandler} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {canUpload && (
      <Box
        {...getRootProps()}
        sx={{
          mb: 3,
          p: 2,
          border: '1px dashed grey',
          borderRadius: 2, 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: 2,
          backgroundColor: isDragActive ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
          transition: 'background-color 0.3s',
          '&:hover': {
            backgroundColor: 'rgba(76, 175, 80, 0.05)',
            cursor: 'pointer'
          }
        }}
      >
        <input 
          {...getInputProps()} 
          id="file-input"
          onChange={handleFileChange} 
          multiple
        />
        <Button 
          component="span" 
          variant="contained" 
          startIcon={<UploadIcon />}
          sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#388e3c" } }}
        >
          Select Files
        </Button>
        <Typography variant="body2" sx={{ flex: 1, ml: 1 }}>
          {isDragActive 
            ? 'Drop the files here...' 
            : selectedFiles.length > 0 
              ? `${selectedFiles.length} file(s) selected: ${selectedFiles.map(f => f.name).join(', ')}` 
              : 'Drag and drop files here, or click to select files'}
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleUpload} 
          disabled={!selectedFiles.length || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
        >
          Upload
        </Button>
      </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <PamGrid
          ref={gridRef}
          rowData={fileList}
          columnDefs={columnDefs}
          defaultColDef={{ editable: false }}
          height={500}
          isLoading={loading}
          loadingMessage="Loading files..."
          showToolbar
          customToolbarComponents={gridConfigComponents}
          showQuickFilter={true}
          quickFilterText={quickFilterText}
          onQuickFilterChange={setQuickFilterText}
          quickFilterPlaceholder="Search files..."
          showFooter={false}
          pagination={true}
          paginationPageSize={100}
          suppressDragLeaveHidesColumns={true}
          sortingOrder={['asc', 'desc', null]}
          suppressColumnVirtualisation={true}
          onGridReady={gridConfigs.onGridReady}
          // Mirror every column/sort/filter gesture into the local stash so
          // unsaved tweaks survive a page refresh. PamGrid surfaces
          // onFilterChanged at the top level; the column events ride through
          // agGridProps as the stashBindings bundle.
          onFilterChanged={gridConfigs.onGridStateChanged}
          agGridProps={gridConfigs.stashBindings}
          onRowDoubleClicked={(params) => {
            if (isDirectory(params.data)) {
              navigateToDirectory(params.data.path);
            } else {
              handleDownload(params.data.path);
            }
          }}
        />
      </Box>
      
    </Container>
  );
}

export default Browser;
